import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { blueBtnStyle, inputStyle as sharedInputStyle, labelStyle as sharedLabelStyle } from '../../styles/admin-styles';
import { SECTORS } from '../../config/defaults';
import type { StoredSectorTemplate, OrgSector } from '../../api/keycloak-admin';
import { OrgTreeView } from '../../components/OrgTreeView';
import { SectorGroup, groupBySector, formatSector } from '../../components/TemplateListItem';
import { loadTemplates, createTemplate, deleteTemplate } from '../../services/template-service';
import { deepCloneWithNewIds } from '../../utils/org-tree-helpers';

/** Template management admin page */
export function TemplateManagement(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [templates, setTemplates] = useState<StoredSectorTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await loadTemplates(token);
      setTemplates(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async (id: string) => {
    try {
      setError(null);
      await deleteTemplate(token, id);
      setConfirmDeleteId(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleClone = async (template: StoredSectorTemplate) => {
    try {
      setError(null);
      await createTemplate(token, {
        sector: template.sector,
        typeName: `${template.typeName} (Copy)`,
        suggestedPlugins: [...template.suggestedPlugins],
        tree: deepCloneWithNewIds(template.tree),
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const previewTemplate = templates.find((t) => t.id === previewId);
  const grouped = groupBySector(templates);
  const iStyle = sharedInputStyle(isDark, colors);
  const lStyle = sharedLabelStyle(colors);

  if (loading) {
    return <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading templates...</p>;
  }

  if (error && templates.length === 0) {
    return (
      <div>
        <h2 style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: '0 0 16px 0' }}>
          Templates
        </h2>
        <div style={{ padding: '24px', borderRadius: '10px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <p style={{ fontFamily: FONTS.body, fontSize: '14px', color: '#EF4444', marginBottom: '12px' }}>{error}</p>
          <button onClick={refresh} style={{ padding: '8px 16px', borderRadius: '6px', border: `1px solid ${colors.border}`, backgroundColor: 'transparent', color: colors.text, fontFamily: FONTS.body, fontSize: '12px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          Templates
        </h2>
        <button onClick={() => setShowCreate(true)} style={blueBtnStyle(isDark, false)}>
          + New Template
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          backgroundColor: isDark ? '#3b1c1c' : '#fde8e8',
          border: '1px solid #EF4444', color: '#EF4444',
          fontFamily: FONTS.body, fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {showCreate && (
        <CreateTemplateForm
          isDark={isDark} colors={colors} iStyle={iStyle} lStyle={lStyle}
          token={token}
          onCreated={() => { setShowCreate(false); refresh(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div className="vx-fill-col" style={{ flex: '1 1 400px', minWidth: '300px' }}>
          {Object.entries(grouped).map(([sector, items]) => (
            <SectorGroup key={sector} sector={sector} templates={items}
              isDark={isDark} colors={colors}
              previewId={previewId} confirmDeleteId={confirmDeleteId}
              onPreview={setPreviewId} onClone={handleClone}
              onConfirmDelete={setConfirmDeleteId}
              onDelete={handleDelete}
              onCancelDelete={() => setConfirmDeleteId(null)}
            />
          ))}
        </div>

        {previewTemplate && (
          <div className="vx-fill-col" style={{
            flex: '1 1 320px', minWidth: '280px',
            border: `1px solid ${colors.border}`, borderRadius: '10px',
            padding: '16px', maxHeight: '600px', overflow: 'auto',
            backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
          }}>
            <div style={{ fontFamily: FONTS.heading, fontSize: '14px', color: colors.text, letterSpacing: '0.05em', marginBottom: '12px' }}>
              Preview: {previewTemplate.typeName}
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginBottom: '8px' }}>
              Plugins: {previewTemplate.suggestedPlugins.join(', ') || 'None'}
            </div>
            <OrgTreeView
              nodes={previewTemplate.tree}
              selectedId={null}
              onSelect={() => {}}
              onAddChild={() => {}}
              onDelete={() => {}}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Create template inline form */
function CreateTemplateForm({ isDark, colors, iStyle, lStyle, token, onCreated, onCancel }: {
  isDark: boolean; colors: ColorTheme;
  iStyle: React.CSSProperties; lStyle: React.CSSProperties;
  token: string;
  onCreated: () => void; onCancel: () => void;
}): React.JSX.Element {
  const [sector, setSector] = useState<OrgSector | ''>('');
  const [typeName, setTypeName] = useState('');
  const [pluginsInput, setPluginsInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!sector || !typeName) return;
    try {
      setSaving(true);
      await createTemplate(token, {
        sector,
        typeName,
        suggestedPlugins: pluginsInput.split(',').map((s) => s.trim()).filter(Boolean),
        tree: [],
      });
      onCreated();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div style={{
      border: `1px solid ${colors.border}`, borderRadius: '10px',
      padding: '16px', marginBottom: '16px',
      backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
    }}>
      <div style={{ fontFamily: FONTS.heading, fontSize: '14px', color: colors.text, letterSpacing: '0.05em', marginBottom: '12px' }}>
        New Custom Template
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
        <div className="vx-full-field" style={{ flex: '0 0 180px' }}>
          <div style={lStyle}>Sector</div>
          <select value={sector} onChange={(e) => setSector(e.target.value as OrgSector)}
            style={{ ...iStyle, cursor: 'pointer' }}>
            <option value="">Select Sector</option>
            {SECTORS.map((s) => (
              <option key={s} value={s}>{formatSector(s)}</option>
            ))}
          </select>
        </div>
        <div className="vx-full-field" style={{ flex: '1 1 200px' }}>
          <div style={lStyle}>Template Name</div>
          <input type="text" value={typeName} onChange={(e) => setTypeName(e.target.value)}
            placeholder="e.g. Space Launch Delta 45" style={iStyle} />
        </div>
        <div className="vx-full-field" style={{ flex: '1 1 200px' }}>
          <div style={lStyle}>Plugins (comma-separated)</div>
          <input type="text" value={pluginsInput} onChange={(e) => setPluginsInput(e.target.value)}
            placeholder="e.g. space, range-ops" style={iStyle} />
        </div>
        <button onClick={handleSubmit} disabled={!sector || !typeName || saving}
          style={blueBtnStyle(isDark, !sector || !typeName || saving)}>
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button onClick={onCancel} style={{
          height: '32px', padding: '0 12px', borderRadius: '6px',
          border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
          color: colors.text, fontFamily: FONTS.body, fontSize: '11px', cursor: 'pointer',
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
